import {
  jalaliThreeMonthsRange,
  MENU_ENGINEERING_GROUP_LABELS,
  type MenuEngineeringFoodItem,
  type MenuEngineeringGroup,
} from "@kitchen/schemas";
import { Button, Card, Chip, faDigits, formatMoney, Seg } from "@kitchen/ui";
import { useQuery } from "@tanstack/react-query";
import { useId, useMemo, useState } from "react";
import { api, getMenuEngineeringReport, type SiteRow } from "../../api.js";

type GroupTab = "all" | MenuEngineeringGroup | "incomplete";

export function MenuEngineeringPage() {
  const defaultRange = useMemo(() => jalaliThreeMonthsRange(), []);
  const [from, setFrom] = useState<string>(defaultRange.startIso);
  const [to, setTo] = useState<string>(defaultRange.endIso);
  const [selectedKitchenId, setSelectedKitchenId] = useState<string>("");
  const [activeTab, setActiveTab] = useState<GroupTab>("all");
  const [hoveredItem, setHoveredItem] = useState<MenuEngineeringFoodItem | null>(null);

  const fromId = useId();
  const toId = useId();
  const kitchenSelectId = useId();

  // Load kitchens list
  const sitesQuery = useQuery({
    queryKey: ["sites"],
    queryFn: () => api<SiteRow[]>("/sites"),
  });
  const kitchens = useMemo(
    () => (sitesQuery.data ?? []).filter((s) => s.kind === "kitchen" && s.active),
    [sitesQuery.data],
  );

  // Load menu engineering report
  const query = useQuery({
    queryKey: ["menu-engineering", from, to, selectedKitchenId],
    queryFn: () =>
      getMenuEngineeringReport({
        from: from || undefined,
        to: to || undefined,
        kitchenId: selectedKitchenId || undefined,
      }),
  });

  const report = query.data;

  // Filtered items based on activeTab
  const displayedItems = useMemo(() => {
    if (!report) return [];
    if (activeTab === "all") return report.items;
    if (activeTab === "incomplete") return [];
    return report.groups[activeTab];
  }, [report, activeTab]);

  return (
    <div className="stack" style={{ gap: "var(--s5)" }}>
      {/* Top Header */}
      <div className="row wrap between" style={{ alignItems: "center" }}>
        <div>
          <h1 style={{ fontSize: "var(--t28)", fontWeight: 700, margin: 0 }}>مهندسی منو</h1>
          <p style={{ color: "var(--c-ink-soft)", margin: "4px 0 0 0", fontSize: "var(--t14)" }}>
            تحلیل سودآوری و محبوبیت غذاها (ستاره، اسب کاری، معما، سگ)
          </p>
        </div>
      </div>

      {/* Filter Card */}
      <Card>
        <div className="row wrap" style={{ alignItems: "flex-end", gap: "var(--s4)" }}>
          <div className="field" style={{ minWidth: "160px" }}>
            <label htmlFor={fromId}>از تاریخ</label>
            <input
              id={fromId}
              type="date"
              className="input"
              value={from}
              onChange={(e) => {
                setFrom(e.target.value);
              }}
            />
          </div>

          <div className="field" style={{ minWidth: "160px" }}>
            <label htmlFor={toId}>تا تاریخ</label>
            <input
              id={toId}
              type="date"
              className="input"
              value={to}
              onChange={(e) => {
                setTo(e.target.value);
              }}
            />
          </div>

          <div className="field" style={{ minWidth: "200px" }}>
            <label htmlFor={kitchenSelectId}>آشپزخانه</label>
            <select
              id={kitchenSelectId}
              className="input"
              value={selectedKitchenId}
              onChange={(e) => {
                setSelectedKitchenId(e.target.value);
              }}
            >
              <option value="">همه آشپزخانه‌ها</option>
              {kitchens.map((k) => (
                <option key={k.id} value={k.id}>
                  {k.name}
                </option>
              ))}
            </select>
          </div>

          <Button
            variant="ghost"
            onClick={() => {
              setFrom(defaultRange.startIso);
              setTo(defaultRange.endIso);
              setSelectedKitchenId("");
            }}
          >
            بازه پیش‌فرض (۳ ماه گذشته)
          </Button>
        </div>
      </Card>

      {/* Loading & Error States */}
      {query.isLoading && (
        <Card>
          <div style={{ textAlign: "center", padding: "var(--s6)", color: "var(--c-ink-soft)" }}>
            در حال بارگذاری اطلاعات مهندسی منو...
          </div>
        </Card>
      )}

      {query.isError && (
        <Card>
          <div style={{ textAlign: "center", padding: "var(--s6)", color: "var(--c-coral-ink)" }}>
            خطا در دریافت اطلاعات مهندسی منو. لطفاً سطح دسترسی خود را بررسی کنید.
          </div>
        </Card>
      )}

      {/* Content when report is loaded */}
      {report && (
        <>
          {/* Summary Cards */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: "var(--s4)",
            }}
          >
            <Card>
              <div style={{ fontSize: "var(--t12)", color: "var(--c-ink-soft)" }}>کل پرس‌های فروخته‌شده</div>
              <div style={{ fontSize: "var(--t24)", fontWeight: 700, marginTop: "4px" }}>
                {faDigits(report.averages.totalPortions)} <span style={{ fontSize: "var(--t14)" }}>پرس</span>
              </div>
            </Card>

            <Card>
              <div style={{ fontSize: "var(--t12)", color: "var(--c-ink-soft)" }}>
                میانگین وزنی حاشیه سود هر پرس
              </div>
              <div
                style={{
                  fontSize: "var(--t24)",
                  fontWeight: 700,
                  marginTop: "4px",
                  color: "var(--c-blue-ink)",
                }}
              >
                {formatMoney(report.averages.weightedAverageMarginRial)}{" "}
                <span style={{ fontSize: "var(--t14)" }}>ریال</span>
              </div>
            </Card>

            <Card>
              <div style={{ fontSize: "var(--t12)", color: "var(--c-ink-soft)" }}>حد نصاب محبوبیت فروش</div>
              <div style={{ fontSize: "var(--t24)", fontWeight: 700, marginTop: "4px" }}>
                {faDigits(report.averages.popularityThresholdPercent)}٪{" "}
                <span style={{ fontSize: "var(--t14)" }}>از میانگین سهم</span>
              </div>
            </Card>

            <Card>
              <div style={{ fontSize: "var(--t12)", color: "var(--c-ink-soft)" }}>غذاهای تحلیل‌شده</div>
              <div style={{ fontSize: "var(--t24)", fontWeight: 700, marginTop: "4px" }}>
                {faDigits(report.averages.validFoodsCount)}{" "}
                <span style={{ fontSize: "var(--t14)", color: "var(--c-ink-soft)" }}>
                  {report.averages.incompleteFoodsCount > 0
                    ? `(+ ${faDigits(report.averages.incompleteFoodsCount)} ناقص)`
                    : "غذا"}
                </span>
              </div>
            </Card>
          </div>

          {/* Quadrant Chart (Plain SVG) */}
          <Card>
            <div className="stack" style={{ gap: "var(--s3)" }}>
              <div className="row wrap between" style={{ alignItems: "center" }}>
                <h2 style={{ fontSize: "var(--t16)", fontWeight: 700, margin: 0 }}>
                  نمودار ماتریس مهندسی منو (محبوبیت × حاشیه سود)
                </h2>
                {hoveredItem && (
                  <div style={{ fontSize: "var(--t14)", fontWeight: 500, color: "var(--c-blue-ink)" }}>
                    {hoveredItem.foodName}: {hoveredItem.explanation}
                  </div>
                )}
              </div>

              <div style={{ width: "100%", overflowX: "auto" }}>
                <MenuEngineeringSvgChart
                  items={report.items}
                  averages={report.averages}
                  onHoverItem={setHoveredItem}
                />
              </div>

              {/* Chart Legend */}
              <div
                className="row wrap center"
                style={{
                  gap: "var(--s5)",
                  justifyContent: "center",
                  fontSize: "var(--t12)",
                  color: "var(--c-ink-soft)",
                  marginTop: "var(--s2)",
                }}
              >
                <div className="row" style={{ alignItems: "center", gap: "6px" }}>
                  <span
                    style={{
                      width: 12,
                      height: 12,
                      borderRadius: "50%",
                      background: "#2563eb",
                      display: "inline-block",
                    }}
                  />
                  <span>ستاره ({faDigits(report.groups.star.length)})</span>
                </div>
                <div className="row" style={{ alignItems: "center", gap: "6px" }}>
                  <span
                    style={{
                      width: 12,
                      height: 12,
                      borderRadius: "50%",
                      background: "#059669",
                      display: "inline-block",
                    }}
                  />
                  <span>اسب کاری ({faDigits(report.groups.plowhorse.length)})</span>
                </div>
                <div className="row" style={{ alignItems: "center", gap: "6px" }}>
                  <span
                    style={{
                      width: 12,
                      height: 12,
                      borderRadius: "50%",
                      background: "#d97706",
                      display: "inline-block",
                    }}
                  />
                  <span>معما ({faDigits(report.groups.puzzle.length)})</span>
                </div>
                <div className="row" style={{ alignItems: "center", gap: "6px" }}>
                  <span
                    style={{
                      width: 12,
                      height: 12,
                      borderRadius: "50%",
                      background: "var(--c-coral, #ef4444)",
                      display: "inline-block",
                    }}
                  />
                  <span>سگ ({faDigits(report.groups.dog.length)})</span>
                </div>
              </div>
            </div>
          </Card>

          {/* Group Tabs */}
          <div className="row wrap between" style={{ alignItems: "center" }}>
            <Seg<GroupTab>
              label="فیلتر گروه‌ها"
              value={activeTab}
              onChange={setActiveTab}
              options={[
                { value: "all", label: `همه (${faDigits(report.items.length)})` },
                { value: "star", label: `ستاره (${faDigits(report.groups.star.length)})` },
                { value: "plowhorse", label: `اسب کاری (${faDigits(report.groups.plowhorse.length)})` },
                { value: "puzzle", label: `معما (${faDigits(report.groups.puzzle.length)})` },
                { value: "dog", label: `سگ (${faDigits(report.groups.dog.length)})` },
                ...(report.incomplete.length > 0
                  ? [
                      {
                        value: "incomplete" as const,
                        label: `داده ناقص (${faDigits(report.incomplete.length)})`,
                      },
                    ]
                  : []),
              ]}
            />
          </div>

          {/* Main Table for Valid Foods */}
          {activeTab !== "incomplete" && (
            <Card>
              <div style={{ overflowX: "auto" }}>
                <table className="table" style={{ width: "100%", textAlign: "right" }}>
                  <thead>
                    <tr>
                      <th>نام غذا</th>
                      <th>گروه</th>
                      <th>تعداد پرس</th>
                      <th>سهم فروش</th>
                      <th>حاشیه هر پرس</th>
                      <th>حاشیه کل</th>
                      <th>تحلیل و توضیح</th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayedItems.length === 0 ? (
                      <tr>
                        <td
                          colSpan={7}
                          style={{ textAlign: "center", padding: "var(--s5)", color: "var(--c-ink-soft)" }}
                        >
                          موردی در این گروه وجود ندارد.
                        </td>
                      </tr>
                    ) : (
                      displayedItems.map((item) => (
                        <tr
                          key={item.menuItemId}
                          className={item.group === "dog" ? "dog-row" : ""}
                          style={
                            item.group === "dog"
                              ? {
                                  color: "var(--c-coral-ink, #c0392b)",
                                  backgroundColor: "rgba(239, 68, 68, 0.05)",
                                }
                              : {}
                          }
                        >
                          <td style={{ fontWeight: 600 }}>{item.foodName}</td>
                          <td>
                            <GroupBadge group={item.group} />
                          </td>
                          <td>{faDigits(item.portions)} پرس</td>
                          <td>{faDigits(Math.round(item.popularitySharePerMille / 10))}٪</td>
                          <td style={{ direction: "ltr", textAlign: "right" }}>
                            {formatMoney(item.marginPerPortionRial)} ریال
                          </td>
                          <td style={{ direction: "ltr", textAlign: "right" }}>
                            {formatMoney(item.totalMarginRial)} ریال
                          </td>
                          <td
                            style={{
                              fontSize: "var(--t12)",
                              color: item.group === "dog" ? "var(--c-coral-ink)" : "var(--c-ink-soft)",
                            }}
                          >
                            {item.explanation}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          )}

          {/* Incomplete Data Table */}
          {(activeTab === "all" || activeTab === "incomplete") && report.incomplete.length > 0 && (
            <div className="stack" style={{ gap: "var(--s3)", marginTop: "var(--s3)" }}>
              <div className="row wrap between" style={{ alignItems: "center" }}>
                <h3
                  style={{ fontSize: "var(--t16)", fontWeight: 700, margin: 0, color: "var(--c-ink-soft)" }}
                >
                  اقلام با داده ناقص (کنار گذاشته‌شده از میانگین‌ها)
                </h3>
                <span style={{ fontSize: "var(--t12)", color: "var(--c-ink-soft)" }}>
                  {faDigits(report.incomplete.length)} قلم
                </span>
              </div>

              <Card>
                <div style={{ overflowX: "auto" }}>
                  <table className="table" style={{ width: "100%", textAlign: "right" }}>
                    <thead>
                      <tr>
                        <th>نام غذا</th>
                        <th>تعداد پرس</th>
                        <th>فروش کل</th>
                        <th>بهای تمام‌شده</th>
                        <th>علت نقص داده</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.incomplete.map((item) => (
                        <tr key={item.menuItemId}>
                          <td style={{ fontWeight: 600 }}>{item.foodName}</td>
                          <td>{faDigits(item.portions)} پرس</td>
                          <td>
                            {item.totalRevenueRial != null
                              ? `${formatMoney(item.totalRevenueRial)} ریال`
                              : "ثبت نشده"}
                          </td>
                          <td>
                            {item.totalCostRial != null
                              ? `${formatMoney(item.totalCostRial)} ریال`
                              : "بدون بهای تمام‌شده"}
                          </td>
                          <td>
                            <Chip>{item.explanation}</Chip>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function GroupBadge({ group }: { group: MenuEngineeringGroup }) {
  const label = MENU_ENGINEERING_GROUP_LABELS[group];
  switch (group) {
    case "star":
      return <Chip tone="blue">{label}</Chip>;
    case "plowhorse":
      return <Chip tone="ok">{label}</Chip>;
    case "puzzle":
      return <Chip tone="warn">{label}</Chip>;
    case "dog":
      return <Chip tone="coral">{label}</Chip>;
  }
}

/** Plain SVG 2x2 quadrant chart */
interface ChartProps {
  items: MenuEngineeringFoodItem[];
  averages: {
    totalPortions: number;
    weightedAverageMarginRial: number;
    popularityThresholdPercent: number;
    validFoodsCount: number;
  };
  onHoverItem: (item: MenuEngineeringFoodItem | null) => void;
}

function MenuEngineeringSvgChart({ items, averages, onHoverItem }: ChartProps) {
  const width = 640;
  const height = 360;
  const padding = { top: 30, right: 30, bottom: 40, left: 60 };

  const plotW = width - padding.left - padding.right;
  const plotH = height - padding.top - padding.bottom;

  // Max portions & margin for chart domain scaling
  const maxPortions = useMemo(() => {
    const maxP = Math.max(...items.map((i) => i.portions), 10);
    return Math.ceil(maxP * 1.15);
  }, [items]);

  const maxMargin = useMemo(() => {
    const maxM = Math.max(
      ...items.map((i) => i.marginPerPortionRial),
      averages.weightedAverageMarginRial * 1.2,
      1000,
    );
    return Math.ceil(maxM * 1.15);
  }, [items, averages]);

  const minMargin = useMemo(() => {
    const minM = Math.min(...items.map((i) => i.marginPerPortionRial), 0);
    return minM < 0 ? Math.floor(minM * 1.15) : 0;
  }, [items]);

  // Threshold coords
  const thresholdPortions =
    averages.validFoodsCount > 0
      ? (averages.totalPortions * averages.popularityThresholdPercent) / (averages.validFoodsCount * 100)
      : 0;

  const thresholdX = padding.left + (thresholdPortions / maxPortions) * plotW;
  const marginRange = maxMargin - minMargin || 1;
  const thresholdY =
    padding.top + plotH - ((averages.weightedAverageMarginRial - minMargin) / marginRange) * plotH;

  const colorMap: Record<MenuEngineeringGroup, string> = {
    star: "#2563eb",
    plowhorse: "#059669",
    puzzle: "#d97706",
    dog: "var(--c-coral, #ef4444)",
  };

  return (
    <svg
      viewBox="0 0 640 360"
      style={{
        width: "100%",
        maxHeight: "360px",
        background: "var(--c-surface, #fcfcfc)",
        borderRadius: "var(--r-card, 14px)",
      }}
      role="img"
      aria-label="نمودار ماتریس مهندسی منو"
    >
      {/* Background Quadrant Labels */}
      <rect
        x={padding.left}
        y={padding.top}
        width={plotW}
        height={plotH}
        fill="none"
        stroke="var(--c-border, #e5e7eb)"
        strokeWidth="1"
      />

      {/* Vertical Threshold line */}
      <line
        x1={thresholdX}
        y1={padding.top}
        x2={thresholdX}
        y2={padding.top + plotH}
        stroke="#94a3b8"
        strokeWidth="1.5"
        strokeDasharray="4 4"
      />

      {/* Horizontal Threshold line */}
      <line
        x1={padding.left}
        y1={thresholdY}
        x2={padding.left + plotW}
        y2={thresholdY}
        stroke="#94a3b8"
        strokeWidth="1.5"
        strokeDasharray="4 4"
      />

      {/* Quadrant Titles in background */}
      {/* Top-Right: Star */}
      <text
        x={thresholdX + (padding.left + plotW - thresholdX) / 2}
        y={padding.top + 20}
        textAnchor="middle"
        fontSize="12"
        fill="#93c5fd"
        fontWeight="600"
      >
        ستاره (محبوبیت بالا / سود بالا)
      </text>

      {/* Top-Left: Puzzle */}
      <text
        x={padding.left + (thresholdX - padding.left) / 2}
        y={padding.top + 20}
        textAnchor="middle"
        fontSize="12"
        fill="#fcd34d"
        fontWeight="600"
      >
        معما (محبوبیت پایین / سود بالا)
      </text>

      {/* Bottom-Right: Plowhorse */}
      <text
        x={thresholdX + (padding.left + plotW - thresholdX) / 2}
        y={padding.top + plotH - 10}
        textAnchor="middle"
        fontSize="12"
        fill="#86efac"
        fontWeight="600"
      >
        اسب کاری (محبوبیت بالا / سود پایین)
      </text>

      {/* Bottom-Left: Dog */}
      <text
        x={padding.left + (thresholdX - padding.left) / 2}
        y={padding.top + plotH - 10}
        textAnchor="middle"
        fontSize="12"
        fill="#fca5a5"
        fontWeight="600"
      >
        سگ (محبوبیت پایین / سود پایین)
      </text>

      {/* Axis Labels */}
      <text
        x={padding.left + plotW / 2}
        y={height - 8}
        textAnchor="middle"
        fontSize="11"
        fill="var(--c-ink-soft, #6b7280)"
      >
        محبوبیت (تعداد پرس فروش‌رفته)
      </text>

      <text
        x={-height / 2}
        y={16}
        transform="rotate(-90)"
        textAnchor="middle"
        fontSize="11"
        fill="var(--c-ink-soft, #6b7280)"
      >
        حاشیه سود هر پرس (ریال)
      </text>

      {/* Food Data Dots */}
      {items.map((item) => {
        const cx = padding.left + (item.portions / maxPortions) * plotW;
        const cy = padding.top + plotH - ((item.marginPerPortionRial - minMargin) / marginRange) * plotH;
        const color = colorMap[item.group];

        return (
          <g
            key={item.menuItemId}
            tabIndex={0}
            role="button"
            aria-label={`${item.foodName}: ${item.explanation}`}
            style={{ cursor: "pointer", outline: "none" }}
            onMouseEnter={() => {
              onHoverItem(item);
            }}
            onMouseLeave={() => {
              onHoverItem(null);
            }}
            onFocus={() => {
              onHoverItem(item);
            }}
            onBlur={() => {
              onHoverItem(null);
            }}
          >
            <title>{`${item.foodName} (${MENU_ENGINEERING_GROUP_LABELS[item.group]})\n${item.explanation}`}</title>
            <circle
              cx={cx}
              cy={cy}
              r={7}
              fill={color}
              stroke="#ffffff"
              strokeWidth="2"
              style={{ transition: "r 0.15s ease" }}
            />
            <text
              x={cx}
              y={cy - 10}
              textAnchor="middle"
              fontSize="10"
              fontWeight="500"
              fill="var(--c-ink, #1f2937)"
            >
              {item.foodName}
            </text>
          </g>
        );
      })}
    </svg>
  );
}
